import React, { FC, useMemo, useState } from 'react';
import useMountEffect from '../../hooks/useMountEffect';
import useValueChangeEffect from '../../hooks/useValueChangeEffect';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import { Button, Card, Dropdown, Empty, Menu, Pagination, Table } from 'antd';
import {
	CheckOutlined,
	ClockCircleOutlined,
	CloseCircleOutlined,
	FileTextOutlined,
	LinkOutlined,
	MoreOutlined,
} from '@ant-design/icons';
import { COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS } from '../../actions/compliance_request_instances_actions';
import {
	complianceRequestInstancesRestCrudThunks,
	unlinkComplianceDocumentFromInstance,
	fetchComplianceSummary,
} from '../../thunks/compliance_request_instances_thunks';
import { fetchComplianceClientStatuses } from '../../thunks/compliance_statuses_thunks';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { COMPLIANCE_EXPIRY_STATUSES, ComplianceRequestInstance } from '../../types/ComplianceTypes';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { deriveComplianceExpiryStatus } from '../../utils/ComplianceUtils';
import {
	SUPPLIER_COMPLIANCE_PIPELINE_FILTER_ITEMS,
	COMPLIANCE_EXPIRY_STATUS_COLORS,
	COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES,
} from '../compliance_common/ComplianceConstants';
import { PipelineFilter } from '../QuotesTable/pipelineFilters/pipelineFilters';
import { ComplianceStatusDisplay } from '../compliance_common/ComplianceStatusDisplay';
import { CompliancePartyBadge } from '../compliance_common/CompliancePartyBadge';
import ChooseComplianceDocumentModal from './ChooseComplianceDocumentModal';

const queryString = require('qs');

const OVERVIEW_TARGET_COLLECTION = 'supplierComplianceOverview';
const STATUS_COUNTS_NAME = 'supplierOverviewStatusCounts';
// The Overview groups instances by client (one client row-block, its request
// instances broken out beneath), and there is no client-grouped backend read —
// only the flat /request_instances list. So we fetch every matching instance in a
// single page and group + paginate by CLIENT client-side. Compliance volume per
// supplier is small; this page size is a safety cap.
//
// BACKEND FOLLOW-UP: replace this 500-row client-side grouping with a server-side
// grouped + paginated compliance read — a party page (supplier/client) plus its
// instances, filter-aware — so the grid fetches only the visible page. That removes
// the cap and the client-side grouping here (and the same in the buyer Overview).
const FETCH_PAGINATION = { current: 1, pageSize: 500 };
const CLIENTS_PER_PAGE = 10;

// check / clock / x icons for the derived linked-document expiry status.
const EXPIRY_ICONS = {
	[COMPLIANCE_EXPIRY_STATUSES.UP_TO_DATE]: CheckOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON]: ClockCircleOutlined,
	[COMPLIANCE_EXPIRY_STATUSES.EXPIRED]: CloseCircleOutlined,
};

// The pipeline chips render one count per status, so the count query must ignore
// the currently selected `statuses` — every other filter (client / linked
// document / due date / search) still applies so the chips follow the grid.
const omitStatuses = (allFilters) => {
	const { statuses, ...rest } = allFilters || {};
	return rest;
};

interface ClientGroup {
	buyerCompanyId: number;
	buyerCompanyName: string;
	instances: ComplianceRequestInstance[];
}

// A flattened table row: one request instance, tagged with the client group it
// belongs to so the Client column can render the group header + rowSpan.
interface GroupedInstanceRow extends ComplianceRequestInstance {
	__clientGroup: ClientGroup;
}

interface SupplierComplianceOverviewPageProps {
	history: any;
	location: any;
	instances: ComplianceRequestInstance[];
	loading: boolean;
	// Backend-owned client party statuses (redux). Refetched into redux by the
	// page's own link/unlink actions, so the badges re-render automatically.
	clientStatuses: any[];
	statusCounts: { [status: string]: number };
	filters: any;
	refetchInstances: (filters: any, pagination: any) => void;
	fetchStatusCounts: (countFilters: any) => Promise<any>;
	fetchClientStatuses: () => Promise<any[]>;
	// Refetches the shell's Non-Compliant summary banner (owned by SupplierCompliancePage
	// via redux) after a link/unlink/replace re-derives client compliance.
	fetchSummary: () => Promise<any>;
	updateFilters: (filters: any, targetCollectionName: string) => void;
	unlinkDocument: (instanceId: number) => Promise<any>;
}

// Backend party-status list -> map keyed by buyerCompanyId so each client header
// can look up its own Compliant / Non-Compliant badge state.
const buildClientStatusMap = (statuses: any[]) =>
	(statuses || []).reduce((accumulated, status) => {
		accumulated[status.buyerCompanyId] = status;
		return accumulated;
	}, {});

// Group the flat instance list into one entry per client, preserving a stable
// (name-sorted) client order. The source list is already filtered server-side, so
// a client only appears if it has a matching instance.
const groupInstancesByClient = (instances: ComplianceRequestInstance[]): ClientGroup[] => {
	const groupsById: { [id: number]: ClientGroup } = {};
	const order: number[] = [];
	(instances || []).forEach((instance) => {
		const buyerCompanyId = instance.buyerCompanyId;
		if (!groupsById[buyerCompanyId]) {
			groupsById[buyerCompanyId] = {
				buyerCompanyId,
				buyerCompanyName:
					nullSafeGet('buyerCompanyName', instance) ||
					nullSafeGetOrElse('buyerCompany.name', instance, '--'),
				instances: [],
			};
			order.push(buyerCompanyId);
		}
		groupsById[buyerCompanyId].instances.push(instance);
	});
	return order
		.map((id) => groupsById[id])
		.sort((left, right) =>
			(left.buyerCompanyName || '').localeCompare(right.buyerCompanyName || '')
		);
};

const SupplierComplianceOverviewPage: FC<SupplierComplianceOverviewPageProps> = ({
	history,
	location,
	instances,
	loading,
	clientStatuses,
	statusCounts,
	filters,
	refetchInstances,
	fetchStatusCounts,
	fetchClientStatuses,
	fetchSummary,
	updateFilters,
	unlinkDocument,
}): React.ReactElement => {
	const [linkingInstance, setLinkingInstance] = useState(null);
	// 'link' opens the chooser for an instance with no doc; 'replace' swaps the
	// currently-linked doc for a different one.
	const [documentDrawerMode, setDocumentDrawerMode] = useState<'link' | 'replace'>('link');
	const [clientPage, setClientPage] = useState(1);

	// Party badges come straight from redux; keyed by buyerCompanyId for per-client
	// lookup in the Client cell.
	const clientStatusMap = useMemo(() => buildClientStatusMap(clientStatuses), [clientStatuses]);

	const statuses = nullSafeGet('statuses', filters);
	// Serialized filter keys — the row fetch follows every filter, the chip counts
	// follow every filter except `statuses`.
	const allFiltersKey = JSON.stringify(filters);
	const countFiltersKey = JSON.stringify(omitStatuses(filters));

	// Refetch the client party statuses into redux (drives the badges). Called on
	// mount and after any instance change (link/unlink) so badges stay in sync.
	const loadClientStatuses = () => fetchClientStatuses();

	// Keep the filters mirrored to the URL query string, matching what
	// PaginatedReduxTable's `updateQueryParams` did: AdvancedFilters derives its
	// initial search from the URL, so without this its clear-search path can fail
	// to refetch. Kept identical to avoid regressing that shared behavior.
	const syncFiltersToUrl = (nextFilters) => {
		const params = queryString.stringify({ ...nextFilters });
		history.replace(`${location.pathname}?${params}`);
	};

	useMountEffect(() => {
		refetchInstances(filters, FETCH_PAGINATION);
		fetchStatusCounts(omitStatuses(filters));
		loadClientStatuses();
	});

	// Rows follow every filter change; jump back to the first client page. Chip
	// counts are refetched by the dedicated countFiltersKey effect below (which also
	// covers status-only changes), so they're intentionally NOT refetched here to
	// avoid a duplicate racing count request.
	useValueChangeEffect(allFiltersKey, () => {
		refetchInstances(filters, FETCH_PAGINATION);
		setClientPage(1);
		syncFiltersToUrl(filters);
	});
	useValueChangeEffect(countFiltersKey, () => {
		fetchStatusCounts(omitStatuses(filters));
	});

	const supplierGroups = useMemo(() => groupInstancesByClient(instances), [instances]);

	// Clamp to a valid page: a link/unlink can shrink the client list below the
	// current page, which would otherwise render an empty table over real data.
	const totalClientPages = Math.max(1, Math.ceil(supplierGroups.length / CLIENTS_PER_PAGE));
	const effectiveClientPage = Math.min(clientPage, totalClientPages);

	// Slice the client groups to the current page, then flatten to instance rows.
	// The first row of each group carries rowSpan = groupSize so the Client cell
	// merges across that client's request rows (antd merged-cell idiom).
	const { rows, rowSpanByInstanceId } = useMemo(() => {
		const start = (effectiveClientPage - 1) * CLIENTS_PER_PAGE;
		const pagedGroups = supplierGroups.slice(start, start + CLIENTS_PER_PAGE);
		const flatRows: GroupedInstanceRow[] = [];
		const spanMap: { [instanceId: number]: number } = {};
		pagedGroups.forEach((group) => {
			group.instances.forEach((instance, indexInGroup) => {
				flatRows.push({ ...instance, __clientGroup: group });
				spanMap[instance.id] = indexInGroup === 0 ? group.instances.length : 0;
			});
		});
		return { rows: flatRows, rowSpanByInstanceId: spanMap };
	}, [supplierGroups, effectiveClientPage]);

	const pipelineValue = { statuses };
	const handlePipelineChange = (nextValue) =>
		updateFilters({ statuses: nullSafeGet('statuses', nextValue) }, OVERVIEW_TARGET_COLLECTION);

	const refreshAfterReview = () => {
		refetchInstances(filters, FETCH_PAGINATION);
		fetchStatusCounts(omitStatuses(filters));
		loadClientStatuses();
		// Link/unlink/replace can flip a client's compliance, so refresh the shell banner.
		fetchSummary();
	};

	const openLinkModal = (instance) => {
		setDocumentDrawerMode('link');
		setLinkingInstance(instance);
	};
	const openReplaceModal = (instance) => {
		setDocumentDrawerMode('replace');
		setLinkingInstance(instance);
	};
	const closeLinkModal = () => setLinkingInstance(null);
	const handleLinked = () => {
		setLinkingInstance(null);
		refreshAfterReview();
	};
	const handleUnlink = (instance) => {
		unlinkDocument(instance.id).then(refreshAfterReview);
	};

	// Client column: one merged cell per client group (Compliant / Non-Compliant
	// badge + name), spanning that client's request rows. The work-order line is
	// read-only here — a supplier can't override a client's acceptance decision.
	const renderClientCell = (row: GroupedInstanceRow) => {
		const group = row.__clientGroup;
		const status = clientStatusMap[group.buyerCompanyId];
		return {
			children: (
				<div className="flex flex-col gap-1">
					<CompliancePartyBadge status={status} perspective="supplier" />
					<span className="bodymedium">{group.buyerCompanyName}</span>
				</div>
			),
			props: { rowSpan: rowSpanByInstanceId[row.id] },
		};
	};

	const renderRequestedCompliance = (instance) => {
		const request = nullSafeGetOrElse('complianceRequest', instance, {});
		return (
			<div className="flex flex-col gap-1">
				<div className="bodymedium">{nullSafeGetOrElse('complianceType.name', request, '--')}</div>
				<div className="footnoteregular flex flex-row items-center gap-1 text-character-secondary">
					<FileTextOutlined />
					{request.sampleFileUrl ? (
						<a href={request.sampleFileUrl} target="_blank" rel="noopener noreferrer">
							Sample
						</a>
					) : null}
					{request.sampleFileUrl ? <span>•</span> : null}
					<span>{request.name}</span>
				</div>
			</div>
		);
	};

	const renderRequestDetails = (instance) => {
		const request = nullSafeGetOrElse('complianceRequest', instance, {});
		const locationLabel = request.allLocations
			? 'All'
			: `${nullSafeGetOrElse('locationIds', request, []).length} locations`;
		return (
			<div className="footnoteregular flex flex-col gap-1 text-character-secondary">
				<span className="bodyregular text-character-primary">
					{request.isMandatory ? 'Mandatory' : 'Optional'}
					{request.requiresUniqueDocument ? ' • Unique Doc' : ''}
				</span>
				<span>{locationLabel}</span>
			</div>
		);
	};

	const renderStatus = (instance) => {
		const dueBy = nullSafeGet('complianceRequest.dueBy', instance);
		return (
			<div className="flex flex-col gap-1">
				<ComplianceStatusDisplay status={instance.status} perspective="supplier" />
				{dueBy ? (
					<span className="footnoteregular text-character-secondary">
						Due: {moment(dueBy).format('D MMM, YYYY')}
					</span>
				) : null}
				{instance.rejectionReason ? (
					<span className="footnoteregular text-character-secondary">
						{instance.rejectionReason}
					</span>
				) : null}
			</div>
		);
	};

	// Expiration Date: the validity window of the document linked to this instance.
	// No server expiryStatus exists, so it is derived from the linked document's
	// expiryDate (Expired if past / Expiring soon within 30 days). Blank until a
	// document is linked. Attention states (soon / expired) get a colored label +
	// icon; an up-to-date document shows just its expiry date.
	const renderExpiry = (instance) => {
		const expiryDate = nullSafeGet('linkedComplianceDocument.expiryDate', instance);
		if (!expiryDate) {
			return <span className="text-character-secondary">--</span>;
		}
		const status = deriveComplianceExpiryStatus(expiryDate);
		const needsAttention =
			status === COMPLIANCE_EXPIRY_STATUSES.EXPIRING_SOON ||
			status === COMPLIANCE_EXPIRY_STATUSES.EXPIRED;
		const color = needsAttention ? COMPLIANCE_EXPIRY_STATUS_COLORS[status] : undefined;
		const IconComponent = needsAttention ? EXPIRY_ICONS[status] : null;
		return (
			<div className="flex flex-col gap-1">
				{needsAttention ? (
					<span className="bodymedium inline-flex flex-row items-center gap-1" style={{ color }}>
						{IconComponent ? <IconComponent /> : null}
						{COMPLIANCE_EXPIRY_STATUS_DISPLAY_NAMES[status]}
					</span>
				) : null}
				<span
					className={needsAttention ? 'footnoteregular' : 'bodyregular'}
					style={color ? { color } : undefined}
				>
					{moment(expiryDate).format('D MMM, YYYY')}
				</span>
			</div>
		);
	};

	const renderDoc = (instance) => {
		const fileUrl = nullSafeGet('linkedComplianceDocument.fileUrl', instance);
		if (fileUrl) {
			return (
				<a
					className="flex min-w-0 flex-row items-center gap-1"
					href={fileUrl}
					target="_blank"
					rel="noopener noreferrer"
					title={nullSafeGetOrElse('linkedComplianceDocument.fileName', instance, 'document')}
				>
					<FileTextOutlined className="shrink-0" />
					<span className="truncate">
						{nullSafeGetOrElse('linkedComplianceDocument.fileName', instance, 'document')}
					</span>
				</a>
			);
		}
		const handleLinkClick = () => openLinkModal(instance);
		return (
			<Button icon={<LinkOutlined />} onClick={handleLinkClick}>
				LINK DOCUMENT
			</Button>
		);
	};

	const renderActions = (instance) => {
		const hasLinkedDocument = !!instance.linkedComplianceDocumentId;
		const sampleFileUrl = nullSafeGet('complianceRequest.sampleFileUrl', instance);
		const handleLinkClick = () => openLinkModal(instance);
		const handleUnlinkClick = () => handleUnlink(instance);
		const handleReplaceClick = () => openReplaceModal(instance);
		const openSample = () => window.open(sampleFileUrl, '_blank', 'noreferrer');
		const menu = (
			<Menu>
				<Menu.Item key="link" disabled={hasLinkedDocument} onClick={handleLinkClick}>
					Link Compliance document
				</Menu.Item>
				{/* Backend allows unlink even from Approved, so gate on "has linked doc". */}
				<Menu.Item key="unlink" disabled={!hasLinkedDocument} onClick={handleUnlinkClick}>
					Unlink Compliance document
				</Menu.Item>
				<Menu.Item key="replace" disabled={!hasLinkedDocument} onClick={handleReplaceClick}>
					Replace Compliance document
				</Menu.Item>
				{sampleFileUrl ? (
					<Menu.Item key="sample" onClick={openSample}>
						View Sample
					</Menu.Item>
				) : null}
			</Menu>
		);
		return (
			<Dropdown overlay={menu} trigger={['click']}>
				<Button icon={<MoreOutlined />} />
			</Dropdown>
		);
	};

	const tableColumns = [
		{
			title: 'Client',
			key: 'client',
			width: '15%',
			render: (text, row: GroupedInstanceRow) => renderClientCell(row),
		},
		{
			title: 'Requested Compliance',
			key: 'requestedCompliance',
			width: '18%',
			render: (text, instance) => renderRequestedCompliance(instance),
		},
		{
			title: 'Request Details',
			key: 'details',
			width: '12%',
			render: (text, instance) => renderRequestDetails(instance),
		},
		{
			title: 'Status',
			key: 'status',
			width: '15%',
			render: (text, instance) => renderStatus(instance),
		},
		{
			title: 'Expiration Date',
			key: 'expiry',
			width: '13%',
			render: (text, instance) => renderExpiry(instance),
		},
		{
			title: 'Compliance Doc',
			key: 'doc',
			width: '15%',
			render: (text, instance) => renderDoc(instance),
		},
		{
			title: '',
			key: 'actions',
			width: 48,
			align: 'right' as const,
			render: (text, instance) => renderActions(instance),
		},
	];

	const pipelineCounts = SUPPLIER_COMPLIANCE_PIPELINE_FILTER_ITEMS.reduce(
		(accumulated, item) => ({
			...accumulated,
			[item.name]: nullSafeGetOrElse(item.name, statusCounts, 0),
		}),
		{}
	);

	const emptyState = (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description="Compliance requests from your clients will appear here."
		/>
	);

	return (
		<div className="flex flex-col gap-4 pb-6">
			<PipelineFilter
				items={SUPPLIER_COMPLIANCE_PIPELINE_FILTER_ITEMS}
				subItems={{}}
				value={pipelineValue}
				onChange={handlePipelineChange}
				counts={pipelineCounts}
			/>
			<Card bordered={false} bodyStyle={{ padding: 8 }}>
				<Table
					dataSource={rows}
					columns={tableColumns}
					rowKey={(row) => row.id}
					loading={loading}
					pagination={false}
					locale={{ emptyText: emptyState }}
				/>
				{supplierGroups.length > CLIENTS_PER_PAGE ? (
					<div className="mt-3 flex flex-row justify-end">
						<Pagination
							current={effectiveClientPage}
							pageSize={CLIENTS_PER_PAGE}
							total={supplierGroups.length}
							showSizeChanger={false}
							onChange={setClientPage}
						/>
					</div>
				) : null}
			</Card>
			{linkingInstance && (
				<ChooseComplianceDocumentModal
					visible={!!linkingInstance}
					mode={documentDrawerMode}
					instance={linkingInstance}
					onCancel={closeLinkModal}
					onSuccess={handleLinked}
				/>
			)}
		</div>
	);
};

const mapStateToProps = (state) => {
	const slice = state.compliance_request_instances;
	return {
		instances: getRecordsForTargetCollection(slice, OVERVIEW_TARGET_COLLECTION),
		loading: nullSafeGetOrElse(`${OVERVIEW_TARGET_COLLECTION}.fetching`, slice, false),
		clientStatuses: nullSafeGetOrElse('clientStatuses', state.compliance_party_statuses, []),
		statusCounts: nullSafeGetOrElse(`counts.${STATUS_COUNTS_NAME}`, slice, {}),
		filters: nullSafeGetOrElse(`${OVERVIEW_TARGET_COLLECTION}.filters`, slice, {}),
	};
};

const mapDispatchToProps = (dispatch) => ({
	// Fetch every matching instance for the current filters into the overview
	// collection. Grouping + client pagination happen client-side, so this is a
	// single generous page (see FETCH_PAGINATION).
	refetchInstances: (filters, pagination) =>
		dispatch(
			complianceRequestInstancesRestCrudThunks(ROLE_TYPES.SUPPLIER).read(
				{},
				OVERVIEW_TARGET_COLLECTION,
				pagination,
				null,
				filters
			)
		),
	fetchStatusCounts: (countFilters) =>
		dispatch(
			complianceRequestInstancesRestCrudThunks(ROLE_TYPES.SUPPLIER).countBy(
				{ countBy: 'status', ...countFilters },
				STATUS_COUNTS_NAME
			)
		),
	updateFilters: (filters, targetCollectionName) =>
		dispatch(
			COMPLIANCE_REQUEST_INSTANCES_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollectionName)
		),
	unlinkDocument: (instanceId) =>
		dispatch(unlinkComplianceDocumentFromInstance(ROLE_TYPES.SUPPLIER)(instanceId)),
	fetchClientStatuses: () => dispatch(fetchComplianceClientStatuses(ROLE_TYPES.SUPPLIER)()),
	fetchSummary: () => dispatch(fetchComplianceSummary(ROLE_TYPES.SUPPLIER)()),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SupplierComplianceOverviewPage)
);
